import { type ReactNode } from "react";
import type { RunHeat, RunRider } from "@/components/run/model";
import { ScoreCorrectionSheet } from "./ScoreCorrectionSheet";
import { PlaceSheet } from "./PlaceSheet";
import { ExcuseSheet } from "./ExcuseSheet";
import { AnchorSheet } from "./AnchorSheet";
import { type OfficialSheetProps, type OfficialTransport } from "./shared";
interface Action {
  label: string;
  render: (close: () => void, saved: () => Promise<void>) => ReactNode;
}
import { ConfirmAction, MessageSheet } from "./CommandSheets";
export function officialActions(
  competitionId: string,
  heat: RunHeat,
  transport: OfficialTransport,
  rider?: RunRider,
): Action[] {
  const base = `/api/competitions/${competitionId}/heats/${heat.id}`;
  const props = (onClose: () => void, onSaved: () => Promise<void>): OfficialSheetProps => ({
    competitionId,
    heat,
    transport,
    onClose,
    onSaved,
  });
  const confirm = (
    label: string,
    path: string,
    body: unknown,
    method?: "put" | "post",
  ): Action => ({
    label,
    render: (close, saved) => (
      <ConfirmAction
        {...props(close, saved)}
        title={label}
        path={path}
        kind={
          path.endsWith("/unrelease") ? "unrelease" : path.endsWith("/lock") ? "lock" : "release"
        }
        body={body}
        method={method}
      />
    ),
  });
  if (rider)
    return [
      {
        label: "Scores",
        render: (close, saved) => <ScoreCorrectionSheet {...props(close, saved)} rider={rider} />,
      },
      ...(heat.judging.head_judge_gate
        ? [confirm("Release rider", `${base}/release`, { heat_rider_id: rider.heat_rider_id })]
        : []),
    ];
  return [
    ...(heat.judging.head_judge_gate
      ? [
          confirm("Release heat", `${base}/release`, { all: true }),
          confirm("Unrelease heat", `${base}/unrelease`, { all: true }),
        ]
      : []),
    ...(heat.judging.judge_placements && heat.placement_pending
      ? [
          {
            label: "Placements",
            render: (close: () => void, saved: () => Promise<void>) => (
              <PlaceSheet {...props(close, saved)} />
            ),
          },
        ]
      : []),
    ...(heat.judge_count >= 2
      ? [
          {
            label: "Judges",
            render: (close: () => void, saved: () => Promise<void>) => (
              <ExcuseSheet {...props(close, saved)} />
            ),
          },
        ]
      : []),
    ...(heat.judging.judges_can_edit_live
      ? [
          confirm("Lock judge edits", `${base}/lock`, { locked: true }, "put"),
          confirm("Unlock judge edits", `${base}/lock`, { locked: false }, "put"),
        ]
      : []),
    ...(heat.judging.anchor_mode && heat.judging.anchor_mode !== "none"
      ? [
          {
            label: "Anchor",
            render: (close: () => void, saved: () => Promise<void>) => (
              <AnchorSheet {...props(close, saved)} />
            ),
          },
        ]
      : []),
    {
      label: "Message judges",
      render: (close, saved) => <MessageSheet {...props(close, saved)} />,
    },
  ];
}
